import { useEffect, useState } from "react";
import { X, Mail, Phone, MapPin, Briefcase, Calendar, Trash2, MessageSquare, PhoneCall, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAGES } from "@/components/LeadKanban";

const fmtDate = (d) => new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const fmtFullDate = (d) => new Date(d).toLocaleString("en-GB");

const STAGE_BY_ID = STAGES.reduce((m, s) => ({ ...m, [s.id]: s }), {});

const ACTIVITY_ICON = {
  note: MessageSquare,
  call: PhoneCall,
  email: Send,
  meeting: Calendar,
  status_change: Briefcase,
  system: Briefcase,
};

export default function LeadDetailDrawer({ lead, onClose, onUpdated, onDeleted }) {
  const [local, setLocal] = useState(lead);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(lead?.notes || "");
  const [valueDraft, setValueDraft] = useState(lead?.deal_value_eur ?? "");
  const [activityType, setActivityType] = useState("note");
  const [activityText, setActivityText] = useState("");
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    setLocal(lead);
    setNotesDraft(lead?.notes || "");
    setValueDraft(lead?.deal_value_eur ?? "");
  }, [lead?.id]);

  if (!lead) return null;
  const stage = local.stage || local.status || "new";
  const stageMeta = STAGE_BY_ID[stage] || STAGES[0];

  const updateStage = async (newStage) => {
    try {
      const r = await api.put(`/admin/leads/${local.id}`, { stage: newStage });
      setLocal(r.data);
      onUpdated(r.data);
      toast.success(`Moved to ${STAGE_BY_ID[newStage].label}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const saveValue = async () => {
    const num = parseFloat(valueDraft);
    if (valueDraft !== "" && (Number.isNaN(num) || num < 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      const r = await api.put(`/admin/leads/${local.id}`, {
        deal_value_eur: valueDraft === "" ? null : num,
      });
      setLocal(r.data);
      onUpdated(r.data);
      toast.success("Value updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const r = await api.put(`/admin/leads/${local.id}`, { notes: notesDraft });
      setLocal(r.data);
      onUpdated(r.data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingNotes(false);
    }
  };

  const logActivity = async () => {
    if (!activityText.trim()) return;
    setLogging(true);
    try {
      const r = await api.post(`/admin/leads/${local.id}/activity`, {
        type: activityType,
        content: activityText.trim(),
      });
      setLocal(r.data);
      onUpdated(r.data);
      setActivityText("");
      toast.success("Activity logged");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Log failed");
    } finally {
      setLogging(false);
    }
  };

  const deleteLead = async () => {
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/leads/${local.id}`);
      toast.success("Lead deleted");
      onDeleted(local.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const activity = Array.isArray(local.activity) ? [...local.activity].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")) : [];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="lead-drawer-backdrop"
        className="fixed inset-0 z-[100]"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)" }}
      />
      {/* Drawer */}
      <div
        className="fixed top-0 right-0 bottom-0 z-[101] flex flex-col"
        style={{
          width: "min(640px, 100vw)",
          background: "var(--ink)",
          borderLeft: "1px solid var(--line)",
          boxShadow: "-30px 0 80px -20px rgba(0,0,0,0.6)",
        }}
        data-testid="lead-drawer"
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-start justify-between gap-4"
          style={{ borderBottom: "1px solid var(--line)" }}
        >
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1"
                style={{ background: `${stageMeta.color}1f`, color: stageMeta.color, border: `1px solid ${stageMeta.color}66`, borderRadius: 3 }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 999, background: stageMeta.color, display: "inline-block" }} />
                {stageMeta.label}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)]">
                {(local.language || "en").toUpperCase()} · {fmtDate(local.created_at)}
              </span>
            </div>
            <h2 className="display text-3xl text-[var(--bone)] leading-tight" data-testid="drawer-lead-name">
              {local.first_name} {local.last_name}
            </h2>
            {local.company && <div className="text-[var(--bone-2)] mt-1">{local.company}</div>}
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="text-[var(--mute)] hover:text-[var(--bone)] p-2 -m-2"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-7" data-lenis-prevent>
          {/* Contact info */}
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center gap-3 text-[var(--bone-2)]">
              <Mail size={14} style={{ color: "var(--pewter)" }} />
              <a href={`mailto:${local.email}`} className="link-under" data-testid="drawer-email">{local.email}</a>
            </div>
            {local.phone && (
              <div className="flex items-center gap-3 text-[var(--bone-2)]">
                <Phone size={14} style={{ color: "var(--pewter)" }} />
                <a href={`tel:${local.phone}`} className="link-under" data-testid="drawer-phone">{local.phone}</a>
              </div>
            )}
            {local.jurisdiction && (
              <div className="flex items-center gap-3 text-[var(--bone-2)]">
                <MapPin size={14} style={{ color: "var(--pewter)" }} />
                {local.jurisdiction}
              </div>
            )}
            {local.service_interest && (
              <div className="flex items-center gap-3 text-[var(--bone-2)]">
                <Briefcase size={14} style={{ color: "var(--pewter)" }} />
                {local.service_interest}
              </div>
            )}
          </div>

          {/* Original message */}
          {local.message && (
            <div
              className="p-4 text-sm text-[var(--bone-2)] leading-relaxed italic"
              style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--line)", borderRadius: 4 }}
            >
              "{local.message}"
            </div>
          )}

          {/* Stage selector */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] mb-3">Stage</div>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => updateStage(s.id)}
                  data-testid={`drawer-stage-${s.id}`}
                  className={`tag ${stage === s.id ? "tag-active" : ""}`}
                >
                  <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: s.color, marginRight: 6 }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Deal value */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] block mb-3">
              Deal value (€)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="500"
                value={valueDraft}
                onChange={(e) => setValueDraft(e.target.value)}
                placeholder="—"
                data-testid="drawer-value-input"
                className="field flex-1"
                style={{ maxWidth: 220 }}
              />
              <button onClick={saveValue} className="btn-secondary" data-testid="drawer-value-save">
                Save
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] block mb-3">
              Notes (private)
            </label>
            <textarea
              rows={4}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Add private notes about this lead…"
              data-testid="drawer-notes"
              className="field w-full resize-y"
            />
            <button
              onClick={saveNotes}
              disabled={savingNotes}
              data-testid="drawer-notes-save"
              className="btn-ghost mt-2 disabled:opacity-50"
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          </div>

          {/* Log activity */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] mb-3">
              Log activity
            </div>
            <div className="flex gap-2 mb-2 flex-wrap">
              {["note", "call", "email", "meeting"].map((typ) => (
                <button
                  key={typ}
                  onClick={() => setActivityType(typ)}
                  data-testid={`drawer-activity-type-${typ}`}
                  className={`tag ${activityType === typ ? "tag-active" : ""}`}
                >
                  {typ}
                </button>
              ))}
            </div>
            <textarea
              rows={2}
              value={activityText}
              onChange={(e) => setActivityText(e.target.value)}
              placeholder="What happened?"
              data-testid="drawer-activity-text"
              className="field w-full resize-y"
            />
            <button
              onClick={logActivity}
              disabled={logging || !activityText.trim()}
              data-testid="drawer-activity-log"
              className="btn-primary mt-2 disabled:opacity-50"
            >
              {logging ? "Logging…" : "Log activity"}
            </button>
          </div>

          {/* Timeline */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)] mb-4">
              Timeline
            </div>
            <div className="space-y-4" data-testid="drawer-timeline">
              {activity.length === 0 && (
                <div className="text-[var(--mute)] text-sm font-mono">No activity yet.</div>
              )}
              {activity.map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || Briefcase;
                return (
                  <div key={i} className="flex gap-3" data-testid={`drawer-activity-row-${i}`}>
                    <div
                      className="shrink-0 mt-0.5 flex items-center justify-center"
                      style={{
                        width: 28, height: 28, borderRadius: 999,
                        background: "rgba(190,170,140,0.1)",
                        border: "1px solid var(--line-2)",
                      }}
                    >
                      <Icon size={12} style={{ color: "var(--pewter)" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--bone-2)] text-sm leading-relaxed whitespace-pre-wrap">
                        {a.content}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)] mt-1">
                        {a.type} · {fmtFullDate(a.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer — delete */}
        <div
          className="px-6 py-4 flex justify-end gap-2"
          style={{ borderTop: "1px solid var(--line)" }}
        >
          <button
            onClick={deleteLead}
            data-testid="drawer-delete"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)] hover:text-[#E07A7A] px-3 py-2"
          >
            <Trash2 size={12} /> Delete lead
          </button>
        </div>
      </div>
    </>
  );
}
